import type { EarlyDeliveryItem } from '../domain/models/EarlyDeliveryItem';
import { EARLY_DELIVERY_PERIOD_DAYS } from '../domain/models/EarlyDeliveryItem';
import { getDeliveryPeriodEnd, isEarlyDeliverable } from './eddFormatting';

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function getEarlyDeliveryItems(
  designEddDetails: unknown | null,
  eddFullDate: string | null,
  periodDays = EARLY_DELIVERY_PERIOD_DAYS
): EarlyDeliveryItem[] {
  if (!designEddDetails || typeof designEddDetails !== 'object') {
    return [];
  }

  if (isEarlyDeliverable(eddFullDate, periodDays)) {
    return [];
  }

  const periodEnd = getDeliveryPeriodEnd(periodDays);
  const itemsByKey = new Map<string, EarlyDeliveryItem>();
  const customizations = designEddDetails as Record<string, Record<string, unknown>>;

  for (const customization of Object.values(customizations)) {
    const skuCodeMap = customization.skuCodeMap as Record<string, Record<string, unknown>> | undefined;
    if (!skuCodeMap) continue;

    const customizationId = readNumber(customization.customizationId);
    if (customizationId == null) continue;

    const metalPurityRaw =
      readString(customization.MetalPurity) ?? readString(customization.metalPurity) ?? '';
    const metalPurity =
      metalPurityRaw && !metalPurityRaw.endsWith('kt') ? `${metalPurityRaw}kt` : metalPurityRaw;

    for (const [sizeKey, sizeEntry] of Object.entries(skuCodeMap)) {
      const deliveryDateInfo = sizeEntry.deliveryDateInfo as Record<string, unknown> | undefined;
      const expectedDeliveryDate = readString(deliveryDateInfo?.expectedDeliveryDate);
      if (!expectedDeliveryDate) continue;

      const edd = new Date(expectedDeliveryDate);
      if (edd > periodEnd) continue;

      const size = String(sizeEntry.size ?? sizeKey);
      const displaySize = String(sizeEntry.displaySize ?? sizeEntry.size ?? sizeKey);
      const id = `${customizationId}_${size}`;

      itemsByKey.set(id, {
        id,
        customizationId,
        metalPurity,
        metalType: readString(customization.metalType) ?? '',
        metalColor: readString(customization.metalColor) ?? '',
        diamondClarity: readString(customization.diamondClarity),
        diamondColor: readString(customization.diamondColor),
        solitaireCarat: readString(customization.solitaireCarat),
        solitaireClarity: readString(customization.solitaireClarity),
        solitaireColor: readString(customization.solitaireColor),
        size: {
          size,
          displaySize,
          expectedDeliveryDate,
        },
      });
    }
  }

  return Array.from(itemsByKey.values());
}
